import { Eye, Languages, Layers, Network } from 'lucide-react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import BezelFrame from '@/Components/Marketing/BezelFrame';
import LazyBoundary from '@/Components/Marketing/LazyBoundary';
import Reveal from '@/Components/Marketing/Reveal';
import Section from '@/Components/Marketing/Section';
import { Heading } from '@/Components/Marketing/Typography';
import useNearViewport from '@/Components/Marketing/useNearViewport';
import { GENEALOGY_HEIGHT, genealogyWindowHeight } from '@/Components/Marketing/Vignettes/genealogyWindow';
import { cross, keyFamily } from '@/lib/keyFamily';

// The genealogy's window, with its story and everything the story shows, is
// a chunk loaded as the section nears the viewport (as the bento cells are):
// the section sits below the fold, and none of it is in the first load.
const loadBatteriesStory = () => import('@/Components/Marketing/Welcome/BatteriesStory');

const ITEMS = [
    { key: 'levels', Icon: Layers },
    { key: 'genealogy', Icon: Network },
    { key: 'visibility', Icon: Eye },
    // Public passports read in any of the 24 official EU languages: values and
    // layout strings are stored per language (App\Support\Language::CODES) and
    // the public page switches between the ones a passport has
    // (Passport/Render.jsx, LanguagePill).
    { key: 'languages', Icon: Languages },
];

/** Each item's title and text. */
export const BATTERIES_COPY = keyFamily('site.home.batteries.{0}.{1}', cross(ITEMS.map(({ key }) => key), ['title', 'text']));

/**
 * The genealogy's window lays the app out 860px wide at every width (drawn
 * smaller on a phone): its stage lays the app out at desktop breakpoints,
 * which a narrower layout would crowd (VignetteFrame).
 */
const BASE_WIDTH = 860;

/**
 * What makes the platform fit how any product is built (its levels and
 * their tiers, each part's genealogy, what is public, every EU language),
 * beside a battery pack's passport in the app as the worked example (a live
 * story: its cells imported from a spreadsheet, the pack released with them,
 * one cell found and its lineage traced). Stacks to one column below lg.
 */
export default function BuiltForBatteries() {
    const { formatMessage } = useIntl();
    const t = id => formatMessage({ id });
    const headingId = useId();
    const [windowRef, near] = useNearViewport();
    const reserve = <WindowReserve baseWidth={BASE_WIDTH} />;

    return (
        <Section tone="light" aria-labelledby={headingId} className="border-t border-mk-line">
            <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-16">
                <div className="lg:col-span-5">
                    <Reveal>
                        <Heading id={headingId} className="max-w-md">
                            {t('site.home.batteries.title')}
                        </Heading>
                    </Reveal>
                    <ul role="list" className="mt-10 grid gap-8 sm:mt-12">
                        {ITEMS.map(({ key, Icon }, index) => (
                            <Reveal as="li" key={key} delay={index * 0.06} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4">
                                <span className="flex size-10 items-center justify-center rounded-lg bg-mk-orange-soft text-mk-orange ring-1 ring-inset ring-mk-orange-ink/10">
                                    <Icon aria-hidden="true" strokeWidth={1.5} className="size-5" />
                                </span>
                                <div>
                                    <h3 className="text-base font-semibold leading-6 text-mk-ink">
                                        {t(BATTERIES_COPY.key(key, 'title'))}
                                    </h3>
                                    <p className="mt-1 max-w-[46ch] text-[15px] leading-relaxed text-pretty text-mk-body">
                                        {t(BATTERIES_COPY.key(key, 'text'))}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>

                {/* No Reveal: the story opens on a complete state and plays
                    once a third of it is in view. Until its chunk is there,
                    the window's exact box is held, so nothing moves when it
                    arrives. */}
                <BezelFrame className="lg:col-span-7">
                    <div ref={windowRef} data-batteries-window="">
                        {near ? (
                            <LazyBoundary load={loadBatteriesStory} fallback={reserve}>
                                {(BatteriesStory) => <BatteriesStory baseWidth={BASE_WIDTH} />}
                            </LazyBoundary>
                        ) : reserve}
                    </div>
                </BezelFrame>
            </div>
        </Section>
    );
}

/**
 * The genealogy window's box before its chunk arrives: a white plate exactly
 * the height the window will take (VignetteFrame fits the window by its
 * frame's `clientWidth`; so is this, before the first paint and on every
 * resize), CSS's aspect-ratio standing in until it has measured.
 */
function WindowReserve({ baseWidth }) {
    const ref = useRef(null);
    const [height, setHeight] = useState(null);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;
        const measure = () => setHeight(genealogyWindowHeight(element.clientWidth, baseWidth));
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => observer.disconnect();
    }, [baseWidth]);

    return (
        <div
            ref={ref}
            aria-hidden="true"
            data-batteries-reserve=""
            className="w-full bg-white"
            style={height === null
                ? { aspectRatio: `${baseWidth} / ${GENEALOGY_HEIGHT[baseWidth]}`, maxHeight: GENEALOGY_HEIGHT[baseWidth] }
                : { height }}
        />
    );
}
